import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ShoppingCart, Loader2, AlertCircle, Package, Tag, Building, DollarSign, Info, Calendar, CreditCard, Calculator, ExternalLink, Star, Copy } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';

interface SubscriptionPlanPrice {
  id: string;
  subscription_plan_id: string;
  currency: string;
  price_amount: number;
  cost_amount?: number;
  margin?: number;
}

interface SubscriptionPlanFamily {
  id: string;
  name: string;
  description: string | null;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  family_id: string;
  sku?: string;
  vendor_id?: string;
  vendor_subscription_name?: string;
  vendor_part_number?: string;
  pricing_model?: string;
  unit_measure?: string;
  minimum_quantity?: number;
  maximum_quantity?: number;
  billing_model?: string;
  billing_period_type?: string;
  billing_period_duration?: number;
  subscription_period_type?: string;
  subscription_period_duration?: number;
  service_type?: string;
  billing_cycles?: string;
  trial_period_days?: number;
  prices?: SubscriptionPlanPrice[];
  family?: SubscriptionPlanFamily;
  created_at?: string;
  updated_at?: string;
}

export default function ProductCatalogDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [product, setProduct] = useState<SubscriptionPlan | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('subscription_plans')
        .select(`
          *,
          prices:subscription_plan_prices (*),
          family:subscription_plan_families (*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setProduct(data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar produto',
        description: 'Não foi possível carregar os detalhes do produto. Por favor, tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatBillingFrequency = (frequency: string) => {
    switch (frequency) {
      case 'month':
        return 'Mensal';
      case 'quarter':
        return 'Trimestral';
      case 'semiannual':
        return 'Semestral';
      case 'year':
        return 'Anual';
      case 'one-time':
        return 'Pagamento Único';
      default:
        return frequency;
    }
  };

  const formatBillingModel = (model?: string) => {
    if (!model) return '-';
    
    switch (model) {
      case 'on creation':
        return 'Na criação';
      case 'on activation':
        return 'Na ativação';
      case 'on usage':
        return 'No uso';
      case 'subscription':
        return 'Assinatura';
      case 'one-time':
        return 'Pagamento Único';
      default:
        return model;
    }
  };

  const formatPricingModel = (model?: string, unitMeasure?: string) => {
    if (!model) return '-';
    
    switch (model) {
      case 'flat':
        return 'Valor Fixo';
      case 'per unit':
        return `Por Unidade${unitMeasure ? ` (${formatUnitMeasure(unitMeasure)})` : ''}`;
      default:
        return model;
    }
  };

  const formatBillingPeriod = (type?: string, duration?: number) => {
    if (!type || !duration) return '-';
    
    const typeLabel = type === 'month' ? 'Mês(es)' : type === 'year' ? 'Ano(s)' : type;
    return `${duration} ${typeLabel}`;
  };

  const formatUnitMeasure = (unitMeasure?: string) => {
    if (!unitMeasure) return '-';
    
    const unitMap: Record<string, string> = {
      'user': 'Por usuário',
      'hour': 'Por hora',
      'gb': 'Por GB',
      'license': 'Por licença',
      'seat': 'Por assento',
      'instance': 'Por instância',
      'api_call': 'Por chamada de API',
      'transaction': 'Por transação'
    };
    
    return unitMap[unitMeasure.toLowerCase()] || unitMeasure;
  };

  const handleCopyProductInfo = () => {
    if (!product) return;
    
    const productInfo = `
Produto: ${product.name}
SKU: ${product.sku || 'N/A'}
Descrição: ${product.description || 'N/A'}
Preço: ${product.prices && product.prices.length > 0 
  ? formatCurrency(product.prices[0].price_amount, product.prices[0].currency)
  : 'Não definido'
}
Família: ${product.family?.name || 'Sem família'}
Status: ${product.is_active ? 'Ativo' : 'Inativo'}
    `.trim();

    navigator.clipboard.writeText(productInfo);
    toast({
      title: 'Informações copiadas',
      description: 'As informações do produto foram copiadas para a área de transferência.',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFCFD]">
        {/* Hero Section with Gradient Background */}
        <div className="relative bg-[#1F1746] overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 container mx-auto px-6 py-16">
            <div className="flex flex-col items-center justify-center text-center text-white">
              <Loader2 className="h-16 w-16 animate-spin mb-6" />
              <h1 className="text-2xl font-extrabold mb-2">Carregando Produto</h1>
              <p className="text-white/80">Aguarde enquanto buscamos os detalhes...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#FCFCFD]">
        {/* Hero Section with Gradient Background */}
        <div className="relative bg-[#1F1746] overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 container mx-auto px-6 py-16">
            <div className="flex flex-col items-center justify-center text-center text-white">
              <AlertCircle className="h-16 w-16 mb-6" />
              <h1 className="text-2xl font-extrabold mb-2">Produto Não Encontrado</h1>
              <p className="text-white/80 mb-6">O produto solicitado não foi encontrado em nosso catálogo.</p>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/product-catalog')}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar ao Catálogo
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFCFD]">
      {/* Hero Section with Dynamic Gradient Based on Status */}
      <div className="relative bg-[#1F1746] overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full transform translate-x-48 -translate-y-48" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full transform -translate-x-32 translate-y-32" />
        
        <div className="relative z-10 container mx-auto px-6 py-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-white">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => navigate('/product-catalog')}
                  className="text-white hover:bg-white/20 border-white/30"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="h-8 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-6 w-6" />
                  <span className="text-white/80">Catálogo de Produtos</span>
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-extrabold mb-3">
                {product.name}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-white/90 mb-4">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  <span>{product.family?.name || 'Sem família'}</span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  <span className="font-bold">
                    {product.prices && product.prices.length > 0 
                      ? formatCurrency(product.prices[0].price_amount, product.prices[0].currency)
                      : 'Preço não definido'
                    }
                  </span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <Badge 
                  className={`${product.is_active 
                    ? 'bg-white/20 text-white border-white/30' 
                    : 'bg-red-500/20 text-red-100 border-red-300/30'
                  }`}
                >
                  {product.is_active ? 'Disponível' : 'Indisponível'}
                </Badge>
              </div>
              
              {product.description && (
                <p className="text-white/80 text-lg max-w-2xl">
                  {product.description}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={handleCopyProductInfo}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar Info
              </Button>
              
              <Button
                onClick={() => navigate(`/subscription-plans/details/${product.id}`)}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Ver Plano Completo
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10">
        
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          
          {/* Column 1 - Product Overview */}
          <div className="space-y-6">
            {/* Product Overview */}
            <Card className="border shadow-sm bg-gradient-to-br from-white to-gray-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  Informações do Produto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Nome</span>
                    <span className="text-sm font-extrabold text-right max-w-[200px]">{product.name}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">SKU</span>
                    <span className="text-sm font-extrabold font-mono">{product.sku || 'Não definido'}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Família</span>
                    <span className="text-sm font-extrabold">{product.family?.name || 'Sem família'}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Status</span>
                    <Badge 
                      variant={product.is_active ? "default" : "secondary"}
                      className={product.is_active ? "bg-green-500 hover:bg-green-600" : "bg-gray-500 hover:bg-gray-600"}
                    >
                      {product.is_active ? "Disponível" : "Indisponível"}
                    </Badge>
                  </div>
                </div>

                {product.description && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="text-sm font-extrabold text-gray-600 mb-2">Descrição</h4>
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-gray-900 text-sm leading-relaxed">{product.description}</p>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Vendor Information */}
            <Card className="border shadow-sm bg-gradient-to-br from-white to-purple-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building className="h-5 w-5 text-purple-600" />
                  Informações do Fornecedor
                </CardTitle>
              </CardHeader>
              <CardContent>
                {product.vendor_id ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-gray-500">Nome da Assinatura</span>
                      <span className="text-sm font-extrabold text-right max-w-[150px]">
                        {product.vendor_subscription_name || "-"}
                      </span>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-gray-500">Número da Parte</span>
                      <span className="text-sm font-extrabold font-mono">
                        {product.vendor_part_number || "-"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Building className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Nenhum fornecedor associado</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Column 2 - Pricing Model */}
          <div className="space-y-6">
            {/* Pricing Model */}
            <Card className="border shadow-sm bg-gradient-to-br from-white to-blue-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-blue-600" />
                  Modelo de Precificação
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="p-1 bg-blue-100 rounded-md">
                      <Tag className="h-3 w-3 text-blue-600" />
                    </div>
                    <div className="font-extrabold text-blue-800 text-sm">
                      {formatPricingModel(product.pricing_model, product.unit_measure)}
                    </div>
                  </div>
                  <div className="text-xs text-blue-600">Modelo de precificação</div>
                </div>

                {product.pricing_model === 'per unit' && (
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <h5 className="font-extrabold text-orange-800 mb-2 text-sm">Configuração por Unidade</h5>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-orange-600">Unidade:</span>
                        <span className="font-extrabold text-orange-800">
                          {formatUnitMeasure(product.unit_measure)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-orange-600">Mín:</span>
                        <span className="font-extrabold text-orange-800">
                          {product.minimum_quantity || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-orange-600">Máx:</span>
                        <span className="font-extrabold text-orange-800">
                          {product.maximum_quantity || 'Ilimitada'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Billing Information */}
            <Card className="border shadow-sm bg-gradient-to-br from-white to-orange-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-orange-600" />
                  Informações de Cobrança
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Modelo de Cobrança</span>
                    <span className="text-sm font-extrabold">{formatBillingModel(product.billing_model)}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Período de Cobrança</span>
                    <span className="text-sm font-extrabold">{formatBillingFrequency(product.billing_period_type)}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Duração do Período</span>
                    <span className="text-sm font-extrabold">
                      {formatBillingPeriod(product.billing_period_type, product.billing_period_duration)}
                    </span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm text-gray-500">Período de Assinatura</span>
                    <span className="text-sm font-extrabold">
                      {formatBillingPeriod(product.subscription_period_type, product.subscription_period_duration)}
                    </span>
                  </div>
                  {product.trial_period_days && (
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-gray-500">Período de Teste</span>
                      <span className="text-sm font-extrabold">
                        {product.trial_period_days} dias
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Column 3 - Pricing */}
          <div className="space-y-6">
            {/* Pricing Section */}
            <Card className="border shadow-sm bg-gradient-to-br from-white to-green-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  Preços de Venda
                </CardTitle>
              </CardHeader>
              <CardContent>
                {product.prices && product.prices.length > 0 ? (
                  <div className="space-y-3">
                    {product.prices.map((price, index) => (
                      <div key={price.id} className="p-4 bg-white rounded-lg border border-green-200 shadow-sm">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-green-700 border-green-300 text-xs">
                              {price.currency}
                            </Badge>
                            {index === 0 && (
                              <Badge className="bg-green-500 text-white text-xs">Principal</Badge>
                            )}
                          </div>
                          <div className="text-xl font-extrabold text-green-800">
                            {formatCurrency(price.price_amount, price.currency)}
                          </div>
                        </div>
                        
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <h4 className="font-extrabold text-gray-700 mb-1">Preço não configurado</h4>
                    <p className="text-gray-500 text-sm">Este produto ainda não possui preços definidos</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Product Actions */}
            <Card className="border shadow-sm bg-gradient-to-br from-white to-gray-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-600" />
                  Ações do Produto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  onClick={() => navigate(`/subscription-plans/details/${product.id}`)}
                  className="w-full"
                  size="lg"
                >
                  <Package className="h-4 w-4 mr-2" />
                  Ver Plano Completo
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={handleCopyProductInfo}
                  className="w-full"
                  size="lg"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copiar Informações
                </Button>

                <Button 
                  variant="outline"
                  onClick={() => navigate('/product-catalog')}
                  className="w-full"
                  size="lg"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar ao Catálogo
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Bottom Spacing */}
        <div className="h-8" />
      </div>
    </div>
  );
}
